import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/context/AppContext';
import api from '@/lib/api';
import { openDashboard } from '@/lib/dashboard';
import { LayoutDashboard, Users, Package, Tag, ShoppingBag, BarChart3, Wallet,
  Settings as SettingsIcon, ShieldCheck, ScrollText, ArrowLeftRight, Undo2, Receipt,
  CalendarCheck, Layers, Plus, LogOut, TrendingUp, SlidersHorizontal, Gauge, ExternalLink,
  Database, Activity, UserCog, AlarmClock, Landmark } from 'lucide-react';

const NavItem = ({ to, icon: Icon, label, testid, badge = 0, badgeTone = 'red' }) => (
  <NavLink to={to} data-testid={testid} end={to === '/'} title={label}
    className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? 'bg-primary text-primary-foreground' : 'text-sidebar-muted hover:bg-white/10'}`}>
    <Icon className="h-[18px] w-[18px] shrink-0" /> <span className="sidebar-label truncate">{label}</span>
    {badge > 0 && (
      <span data-testid={`${testid}-badge`}
        className={`sidebar-label ml-auto min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[10px] font-bold leading-none ${badgeTone === 'red' ? 'bg-destructive text-destructive-foreground' : 'bg-amber-500 text-white'}`}>
        {badge > 99 ? '99+' : badge}
      </span>
    )}
  </NavLink>
);

const Group = ({ title }) => (
  <p className="sidebar-label px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted">{title}</p>
);

export default function Sidebar() {
  const { user, logout, stores, currentStore, can, settings } = useApp();
  const navigate = useNavigate();
  const isOwner = user?.role === 'OWNER' || user?.owner_access;
  const managerUp = isOwner || user?.role === 'MANAGER';
  const { data: dueCounts } = useQuery({
    queryKey: ['reminders-badge'],
    queryFn: async () => (await api.get('/reminders?scope=open')).data.data.counts,
    enabled: !!managerUp,
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const dueOverdue = dueCounts?.overdue || 0;
  const dueToday = dueCounts?.today || 0;
  const dueBadge = dueOverdue > 0 ? dueOverdue : dueToday;
  const dueTone = dueOverdue > 0 ? 'red' : 'amber';
  const showAccounting = can('purchases_view') || can('suppliers_view') || can('expenses_view') || can('reports_view') || can('profit_view');
  const storeName = stores.find((s) => s.id === currentStore)?.name || 'Store';
  const branding = settings?.branding || {};
  const logoSizeCls = { small: 'h-8', medium: 'h-10', large: 'h-14' }[branding.logoSize || 'medium'];

  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-[var(--sidebar-width,15rem)] flex-col bg-sidebar transition-[width] duration-200 lg:flex">
      <div className="flex items-center gap-3 px-4 py-4">
        {branding.showInSidebar && branding.logo ? (
          <img src={branding.logo} alt="Logo" className={`${logoSizeCls} w-auto max-w-[70%] rounded object-contain`} data-testid="sidebar-logo" />
        ) : (
          <>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary font-display text-lg font-bold text-primary-foreground">{storeName[0]}</span>
            <div className="sidebar-label min-w-0"><p className="truncate font-display text-sm font-semibold text-sidebar-foreground">{storeName}</p><p className="truncate text-xs text-sidebar-muted">SHABZ CLOSET</p></div>
          </>
        )}
      </div>
      {can('billing_create') && (
        <button data-testid="sidebar-new-bill" onClick={() => navigate('/billing')} title="Create Sales Invoice"
          className="mx-3 mb-2 flex items-center justify-center gap-2 rounded-lg bg-button px-3 py-2.5 text-sm font-semibold text-button-foreground hover:opacity-90">
          <Plus className="h-4 w-4 shrink-0" /> <span className="sidebar-label">Create Sales Invoice</span>
        </button>
      )}
      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        <Group title="General" />
        {can('dashboard') && <NavItem to="/" icon={LayoutDashboard} label="Dashboard" testid="sidebar-dashboard" />}
        {(can('dashboard') || isOwner) && <NavItem to="/management" icon={Gauge} label="Management Dashboard" testid="sidebar-management" />}
        {can('billing_view') && <NavItem to="/billing" icon={Receipt} label="Billing" testid="sidebar-billing" />}
        {can('stock_view') && <NavItem to="/stock" icon={Package} label="Items / Stock" testid="sidebar-stock" />}
        {(can('stock_edit') || can('stock_view') || isOwner) && <NavItem to="/stock-adjustment" icon={SlidersHorizontal} label="Stock Adjustment" testid="sidebar-stock-adjust" />}
        {can('transfer_view') && <NavItem to="/transfers" icon={ArrowLeftRight} label="Transfers" testid="sidebar-transfers" />}
        {(can('returns') || can('exchanges')) && <NavItem to="/returns" icon={Undo2} label="Return / Exchange" testid="sidebar-returns" />}
        {can('customers_view') && <NavItem to="/customers" icon={Users} label="Customers" testid="sidebar-customers" />}
        {can('billing_view') && <NavItem to="/sales" icon={Tag} label="Sales Invoices" testid="sidebar-sales" />}
        {(isOwner || user?.role === 'MANAGER') && <NavItem to="/due-reminders" icon={AlarmClock} label="Due & Reminders" testid="sidebar-due-reminders" badge={dueBadge} badgeTone={dueTone} />}
        {(isOwner || (can && can('reports_view'))) && <NavItem to="/accounts" icon={Landmark} label="Cash & Bank" testid="sidebar-accounts" />}
        {showAccounting && (<>
          <Group title="Accounting" />
          {can('purchases_view') && <NavItem to="/purchases" icon={ShoppingBag} label="Purchases" testid="sidebar-purchases" />}
          {can('suppliers_view') && <NavItem to="/suppliers" icon={Users} label="Suppliers" testid="sidebar-suppliers" />}
          {can('expenses_view') && <NavItem to="/expenses" icon={Wallet} label="Expenses" testid="sidebar-expenses" />}
          {isOwner && <NavItem to="/reports" icon={BarChart3} label="Reports" testid="sidebar-reports" />}
          {isOwner && <NavItem to="/profit" icon={TrendingUp} label="Profit" testid="sidebar-profit" />}
          {isOwner && <NavItem to="/stock-report" icon={Layers} label="Stock Report" testid="sidebar-stock-report" />}
          {isOwner && <NavItem to="/daily-closing" icon={CalendarCheck} label="Daily Closing" testid="sidebar-closing" />}
          {isOwner && <NavItem to="/audit" icon={ScrollText} label="Audit Log" testid="sidebar-audit" />}
        </>)}
        <Group title="System" />
        {(can('users_manage') || isOwner) && <NavItem to="/admin" icon={ShieldCheck} label="Users & Access" testid="sidebar-admin" />}
        {(isOwner || user?.role === 'MANAGER') && <NavItem to="/staff" icon={UserCog} label="Staff & Incentives" testid="sidebar-staff" />}
        {isOwner && <NavItem to="/data-management" icon={Database} label="Data Management" testid="sidebar-data-management" />}
        {isOwner && <NavItem to="/system-health" icon={Activity} label="System Health" testid="sidebar-system-health" />}
        {(can('settings_view') || isOwner) && <NavItem to="/settings" icon={SettingsIcon} label="Settings" testid="sidebar-settings" />}
        <button data-testid="sidebar-logout" onClick={logout} title="Sign out" className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-muted hover:bg-white/10">
          <LogOut className="h-[18px] w-[18px] shrink-0" /> <span className="sidebar-label">Sign out</span>
        </button>
      </nav>
    </aside>
  );
}
